import {
  Component,
  DestroyRef,
  effect,
  inject,
  signal,
  computed,
  Type,
  ViewChild,
  ElementRef,
  HostListener,
  OnDestroy,
  AfterViewInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Tooltip } from 'primeng/tooltip';
import { debounceTime, Subject } from 'rxjs';
import { OmniSearchService } from '../omni-search.service';
import {
  OmniSearchProvider,
  OmniSearchResult,
} from '../models/omni-search.models';
import { AmplitudeService } from '@brighthub/logging';

@Component({
  selector: 'core-search-omni-search',
  standalone: true,
  imports: [CommonModule, FormsModule, Tooltip],
  templateUrl: './omni-search.component.html',
  styleUrl: './omni-search.component.scss',
})
export class OmniSearchComponent implements OnDestroy, AfterViewInit {
  private _search = inject(OmniSearchService);
  private _destroyRef = inject(DestroyRef);
  private _input$ = new Subject<string>();
  private _amplitude = inject(AmplitudeService);
  private _onDocumentClick = (e: MouseEvent) => this._handleDocumentClick(e);
  // Ref to the input (add #searchInput on the <input> in your template)
  @ViewChild('searchInput', { static: false })
  searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('paletteContainer', { static: false })
  paletteContainer?: ElementRef<HTMLElement>;
  // --- Signals ---
  query = signal('');
  showPalette = signal(false);
  showSuggestions = signal(false);
  results = signal<OmniSearchResult[] | null>(null);
  activeProvider = signal<OmniSearchProvider | undefined>(undefined);
  activeComponent = signal<Type<any> | null>(null);
  singleProvider = signal(false);
  suggestedProviders = signal<OmniSearchProvider[]>([]);
  loading = signal(false);

  // Auto-computed from service.providers()
  groupedProviders = computed(() => {
    const providers = this._search.providers(); // <-- reactive signal
    const grouped: { subApp: string; providers: OmniSearchProvider[] }[] = [];
    for (const p of providers) {
      const existing = grouped.find((g) => g.subApp === p.subApp);
      if (existing) existing.providers.push(p);
      else grouped.push({ subApp: p.subApp, providers: [p] });
    }
    return grouped;
  });

  _absoluteIndex(groupIndex: number, providerIndex: number): number {
    const groups = this.groupedProviders();
    let offset = 0;
    for (let i = 0; i < groupIndex; i++) {
      offset += groups[i].providers.length;
    }
    return offset + providerIndex;
  }
  highlightedProviderIndex = signal<number>(-1); // -1 means none
  highlightedIndex = signal<number>(-1);

  @HostListener('window:keydown', ['$event'])
  onKeydown(e: KeyboardEvent) {
    const query = this.query();
    // --- ESC logic (unchanged) ---
    if (e.key === 'Escape') {
      if (this.showPalette() || this.showSuggestions()) {
        this.showPalette.set(false);
        this.showSuggestions.set(false);
        this._blurInputSoon();
        e.stopPropagation();
        return;
      }

      if (this.activeProvider() && !this.query()) {
        this.clearProvider();
        this.showPalette.set(true);
        e.stopPropagation();
        e.preventDefault();
        return;
      }
    }

    // --- navigation for palette or suggestions ---
    const list =
      this.showSuggestions() && this.suggestedProviders().length
        ? this.suggestedProviders()
        : this.showPalette()
          ? this._flattenedProviders()
          : [];

    // If palette or suggestions visible → handle navigation
    if (list.length) {
      if (['ArrowDown', 'ArrowUp', 'Tab', 'Enter'].includes(e.key)) {
        e.preventDefault();
        let newIndex = this.highlightedIndex();

        if (e.key === 'ArrowDown') {
          newIndex = (newIndex + 1) % list.length;
        } else if (e.key === 'ArrowUp') {
          newIndex = newIndex <= 0 ? list.length - 1 : newIndex - 1;
        }

        // select with Tab or Enter
        if ((e.key === 'Tab' || e.key === 'Enter') && list.length) {
          e.preventDefault();

          let index = this.highlightedIndex();
          if (index < 0) index = 0; // fallback to first suggestion

          const item = list[index];
          if (item) {
            this.selectProvider(item, query.startsWith('@'));
            this.highlightedIndex.set(-1);
          }
          return;
        }


        this.highlightedIndex.set(newIndex);
        this._scrollIntoView(newIndex);
        return;
      }
    }

    if (
      e.key === 'Enter' &&
      !this.showPalette() &&
      !this.showSuggestions() &&
      this.activeProvider() &&
      this.query().trim()
    ) {
      e.preventDefault();
      this.onSearch();
    }
  }


// --- flatten providers for palette ---
  private _flattenedProviders(): OmniSearchProvider[] {
    return this.groupedProviders()
      .map((g) => g.providers)
      .reduce((acc, val) => acc.concat(val), []);
  }

// --- scroll the focused item into view ---
  private _scrollIntoView(index: number) {
    const container =
      this.showSuggestions()
        ? document.querySelector('.omni-dropdown')
        : this.paletteContainer?.nativeElement;

    if (!container) return;
    const items = container.querySelectorAll('.palette-item, .dropdown-item');
    const el = items[index] as HTMLElement;
    el?.scrollIntoView({ block: 'nearest' });
  }

  constructor() {
    effect(async () => {
      const providers = this._search.providers();
      if (providers.length === 1 && !this.activeProvider()) {
        const p = providers[0];
        this.activeProvider.set(p);
        this.activeComponent.set(await p.loadComponent());
        this.singleProvider.set(true);
        this.showPalette.set(false);
      }
    });

    // Debounced input handler
    this._input$
      .pipe(debounceTime(150), takeUntilDestroyed(this._destroyRef))
      .subscribe((q) => this._handleInput(q));
  }

  ngAfterViewInit() {
    // attach global click listener once view ready
    document.addEventListener('click', this._onDocumentClick, true);
  }

  ngOnDestroy() {
    document.removeEventListener('click', this._onDocumentClick, true);
  }

  // --- UI Event Handlers ---

  onFocus() {
    if (!this.activeProvider()) this.showPalette.set(true);
  }

  onInputChange() {
    this._input$.next(this.query());
  }

  async selectProvider(p: OmniSearchProvider, clear = false) {
    this.activeProvider.set(p);
    this.activeComponent.set(await p.loadComponent());
    this.showPalette.set(false);
    this.showSuggestions.set(false);
    this._focusInputSoon(); // refocus so user can keep typing
    if (clear) {
      this.query.set('');
    }
  }

  clearProvider() {
    this.activeProvider.set(undefined);
    this.activeComponent.set(null);
    this.results.set(null);
    this._focusInputSoon();
  }

  clearSearch() {
    this.activeProvider.set(undefined);
    this.activeComponent.set(null);
    this.results.set(null);
    this.query.set('');
  }

  // --- private helpers ---

  private _handleDocumentClick(event: MouseEvent) {
    const inputEl = this.searchInput?.nativeElement;
    const paletteEl = this.paletteContainer?.nativeElement;

    if (!inputEl) return;

    const target = event.target as Node;
    const clickedInsideInput = inputEl.contains(target);
    const clickedInsidePalette = paletteEl?.contains(target);

    // Only close if click is outside both input and palette
    if (!clickedInsideInput && !clickedInsidePalette) {
      if (this.showPalette() || this.showSuggestions()) {
        this.showPalette.set(false);
        this.showSuggestions.set(false);
      }
    }
  }

  private async _handleInput(q: string) {
    const activeProvider = this.activeProvider();
    const providers = this._search.providers();

    // Trimmed query for easier checks
    const query = q.trim();

    // If no provider selected yet, keep palette visible
    if (!activeProvider) {
      // Always show palette while no provider selected
      this.showPalette.set(true);
      this.showSuggestions.set(false);

      // Only check shortcuts if query starts with '@'
      if (query.startsWith('@')) {
        const shortcutQuery = query.toLowerCase();
        const suggestions = providers.filter(
          (p: any) => p.shortcut && p.shortcut.toLowerCase().includes(shortcutQuery)
        );

        this.suggestedProviders.set(suggestions);
        this.showSuggestions.set(suggestions.length > 0);

        // ✅ auto-highlight nearest match (prefix match first)
        if (suggestions.length > 0) {
          // look for exact prefix match like @cl matches @client
          const prefixIndex = suggestions.findIndex((p: any) =>
            p.shortcut.toLowerCase().startsWith(shortcutQuery)
          );
          const newIndex = prefixIndex >= 0 ? prefixIndex : 0;
          this.highlightedIndex.set(newIndex);
        } else {
          this.highlightedIndex.set(-1);
        }

        // ✅ auto-select if exact full match typed
        const match = providers.find(
          (p: any) => p.shortcut?.toLowerCase() === shortcutQuery
        );
        if (match) {
          await this.selectProvider(match);
          this.query.set('');
          return;
        }
      }

      // Don’t run search yet — wait for provider selection
      this.results.set(null);
      return;
    }

    // --- Provider selected ---
    this.showPalette.set(false);
    this.showSuggestions.set(false);

    if (!query) {
      this.results.set(null);
    }
  }

  onSearch() {
    const provider = this.activeProvider();
    const q = this.query().trim();

    if (!provider || !q) return;

    this.loading.set(true);
    this.results.set(null);

    provider.search(q).subscribe({
      next: (results) => {
        this._amplitude.logEvent('omni_search_success', {
          provider: provider.label,
          query: q,
          results: results.length
        });
        return this.results.set(results);
      },
      error: (err) => {
        this._amplitude.logEvent('omni_search_failed', {
          provider: provider.label,
          query: q,
          status: err.status
        });
        this.results.set([]);
      },
      complete: () => this.loading.set(false),
    });
  }

  private _focusInputSoon() {
    // ensure DOM has settled before focusing
    setTimeout(() => this.searchInput?.nativeElement.focus(), 0);
  }

  private _blurInputSoon() {
    // ensure DOM has settled before focusing
    setTimeout(() => this.searchInput?.nativeElement.blur(), 0);
  }
}
